import { effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { ShellPanel, ShellPanelService } from '@softapps/core';
import { authorizationFn } from '@softline/auth';
import { translator } from '@softline/ui-core';
import { SOFTLINE_PERMISSION_AI_AGENT } from '../ai-agent.shared';
import { AiAgentPanelComponent } from '../components/panel/panel.component';

const PANEL_ID = 'ai-agent';

/**
 * Registers the chat as a shell trailing panel for permitted users.
 *
 * Root-level rather than `panel()`: that function is DestroyRef-bound to a
 * component, but the panel has to exist on every page the user opens.
 */
@Injectable({ providedIn: 'root' })
export class AiAgentShellPanelService {
  private readonly panelService = inject(ShellPanelService);
  private readonly injector = inject(Injector);
  private readonly t = translator();

  private readonly isAuthorized = authorizationFn()(SOFTLINE_PERMISSION_AI_AGENT);
  private readonly title = this.t('#AI_AGENT.PANEL_TITLE');

  private panel: ShellPanel<unknown> | null = null;

  register(): void {
    effect(
      () => {
        const authorized = this.isAuthorized();
        const title = this.title();
        untracked(() => this.sync(authorized, title));
      },
      { injector: this.injector }
    );
  }

  private sync(authorized: boolean, title: string): void {
    if (!authorized) {
      if (this.panel) this.panelService.unregister(this.panel);
      this.panel = null;
      return;
    }
    if (this.panel) {
      // Title only: re-registering would close the panel when the lazily
      // loaded translation arrives.
      this.panel.title = title;
      return;
    }
    this.panel = {
      type: 'component',
      id: PANEL_ID,
      title,
      icon: 'fa-regular fa-robot',
      position: 'trailing',
      width: '420px',
      component: AiAgentPanelComponent,
    };
    this.panelService.register(this.panel);
  }
}
